import React, { FC, useCallback, useContext } from 'react';
import usePastTime from 'hooks/usePastTime';
import classNames from 'classnames';
import { Button } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import ListingBanner from 'router/subrouters/Dash/subrouters/private/subrouters/Portfolio/subrouters/Seller/subrouters/ListingDetailSeller/subrouters/ListingInformation/components/ManageListingVisibility/components/ListingBanner';
import DraftListingContext from 'providers/DraftListing/DraftListing.context';
import XIcon from 'icons/X.icon';
import api from 'api';
import ClockIcon from 'icons/Clock.icon';
import { useNavigate } from 'react-router-dom';
import { getCreateListingRoute } from 'utils';
import ResolvePopup from 'components/ResolvePopup';
import ArrowrightIcon from 'icons/Arrowright.icon';

import './DraftListingBanner.styles.scss';

type DraftListingBannerProps = {
  className?: string;
};

const DraftListingBanner: FC<DraftListingBannerProps> = ({ className }) => {
  const { hasDraftBanner, draftListing, removeDraftListing } =
    useContext(DraftListingContext);

  const {
    _id: listingId,
    createdAt,
    listingTitle,
    dealRoomId,
    type,
  } = draftListing || {};

  const { t } = useTranslation();
  const navigate = useNavigate();

  const createdDate = usePastTime()(createdAt);

  const classes = classNames('draft-listing-banner', className);

  const onCompleteListing = useCallback(
    () => navigate(getCreateListingRoute(dealRoomId, listingId, type)),
    [dealRoomId, listingId, type, navigate],
  );

  const onSnoozeBanner = useCallback(() => {
    api.portfolio.seller.snoozeDraft(listingId);
    removeDraftListing();
  }, [listingId, removeDraftListing]);

  const onCloseBanner = useCallback(() => {
    api.portfolio.seller.clearDraft(listingId);
    removeDraftListing();
  }, [listingId, removeDraftListing]);

  if (!hasDraftBanner) return null;

  return (
    <ListingBanner
      className={classes}
      title={t('youGotDraftListingAwaiting')}
      description={t('draftStartedTitle', {
        listingTitle,
        date: createdDate,
      })}
    >
      <Button
        icon={ArrowrightIcon}
        iconPosition="right"
        onClick={onCompleteListing}
      >
        {t('completeListing')}
      </Button>
      <ResolvePopup
        onHover
        preferUpwards
        popupContent={t('snooze')}
        contentClassName="draft-listing-banner__popup"
      >
        <Button
          variant="outline"
          icon={<ClockIcon viewBox="2 2 20 20" />}
          onClick={onSnoozeBanner}
        />
      </ResolvePopup>
      <ResolvePopup
        onHover
        preferUpwards
        popupContent={t('deleteDraft')}
        contentClassName="draft-listing-banner__popup"
      >
        <Button variant="outline" icon={XIcon} onClick={onCloseBanner} />
      </ResolvePopup>
    </ListingBanner>
  );
};

export default DraftListingBanner;
